import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import CompanyNode from '@/components/settings/CompanyNode';
import SettingsEmptyState from '@/components/settings/SettingsEmptyState';

function includes(text, query) {
  return String(text || '').toLocaleLowerCase().includes(query);
}

export default function SettingsHierarchy({
  companies = [],
  locationsByCompany = {},
  departmentsByLocation = {},
  searchPlaceholder = 'Cerca azienda, locale o reparto…',
  renderCompany,
  renderDepartment,
  renderLocation,
  compact = false,
  testId = 'settings-hierarchy',
}) {
  const [query, setQuery] = useState('');
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [expandedLocations, setExpandedLocations] = useState(new Set());

  useEffect(() => {
    setExpandedCompanies(compact ? new Set() : new Set(companies.map((company) => company.id)));
    setExpandedLocations(compact ? new Set() : new Set(Object.values(locationsByCompany).flat().map((location) => location.id)));
  }, [companies, locationsByCompany, compact]);

  const filteredCompanies = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return companies;

    return companies.reduce((result, company) => {
      const companyLocations = locationsByCompany[company.id] || [];
      const companyMatches = includes(company.name, normalizedQuery);
      const matchingLocations = companyLocations.reduce((locationResult, location) => {
        const departments = departmentsByLocation[location.id] || [];
        const locationMatches = includes(location.name, normalizedQuery);
        const matchingDepartments = departments.filter((department) => includes(department.name, normalizedQuery));
        if (companyMatches || locationMatches || matchingDepartments.length) {
          locationResult.push(companyMatches || locationMatches
            ? location
            : { ...location, _filteredDepartments: matchingDepartments });
        }
        return locationResult;
      }, []);
      if (companyMatches || matchingLocations.length) {
        result.push(companyMatches ? company : { ...company, _filteredLocations: matchingLocations });
      }
      return result;
    }, []);
  }, [companies, locationsByCompany, departmentsByLocation, query]);

  const toggleCompany = (companyId) => {
    setExpandedCompanies((current) => {
      const next = new Set(current);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const toggleLocation = (locationId) => {
    setExpandedLocations((current) => {
      const next = new Set(current);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  if (!companies.length) {
    return <SettingsEmptyState title="Nessuna azienda disponibile" description="Non risultano aziende nel perimetro autorizzato." testId={`${testId}-empty`} />;
  }

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden="true" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label="Cerca nella gerarchia Impostazioni"
          className="pl-9"
          data-testid={`${testId}-search`}
        />
      </div>

      {filteredCompanies.length ? (
        filteredCompanies.map((company) => {
          const locations = company._filteredLocations || locationsByCompany[company.id] || [];
          const scopedDepartments = { ...departmentsByLocation };
          locations.forEach((location) => {
            if (location._filteredDepartments) scopedDepartments[location.id] = location._filteredDepartments;
          });
          return (
            <CompanyNode
              key={company.id}
              company={company}
              locations={locations}
              departmentsByLocation={scopedDepartments}
              expanded={expandedCompanies.has(company.id) || Boolean(query)}
              onToggle={() => toggleCompany(company.id)}
              expandedLocations={expandedLocations}
              autoExpandLocations={Boolean(query)}
              onToggleLocation={toggleLocation}
              renderCompany={renderCompany}
              renderDepartment={renderDepartment}
              renderLocation={renderLocation}
              compact={compact}
            />
          );
        })
      ) : (
        <SettingsEmptyState
          title="Nessun risultato"
          description="Prova con il nome di un’azienda, di un locale o di un reparto."
          action={<Button type="button" variant="ghost" size="sm" onClick={() => setQuery('')}>Cancella ricerca</Button>}
          testId={`${testId}-no-results`}
        />
      )}
    </div>
  );
}